import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Ban,
  Building2,
  PackageOpen,
  ChevronLeft,
  ChevronRight,
  Settings,
  Save,
  Zap,
  Trash2,
  AlertTriangle,
  Download,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../utils/axios';

const ESTADOS = {
  PENDIENTE: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  CREADA: { label: 'Creada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  ERROR: { label: 'Error', color: 'bg-red-100 text-red-700', icon: XCircle },
  CANCELADA: { label: 'Cancelada', color: 'bg-slate-200 text-slate-600', icon: Ban },
};

const LIMIT_LABELS = {
  limite_diario: 'Límite Diario',
  limite_mensual: 'Límite Mensual',
  limite_alerta: 'Límite Alerta',
  limite_bloqueo_retiro: 'Límite Bloqueo Retiro',
  limite_bloqueo_cuenta: 'Límite Bloqueo Cuenta',
  limite_bloqueo_cvu: 'Límite Bloqueo CVU',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

export default function CuentasWaya() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cuentas, setCuentas] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, pendientes: 0, creadas: 0, error: 0, canceladas: 0 });
  const [filterEstado, setFilterEstado] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [showConfig, setShowConfig] = useState(false);
  const [limitsConfig, setLimitsConfig] = useState({
    limite_diario: '1000000.00',
    limite_mensual: '500000000.00',
    limite_alerta: '20000000.00',
    limite_bloqueo_retiro: '25000000.00',
    limite_bloqueo_cuenta: '20000000.00',
    limite_bloqueo_cvu: '23000000.00',
    aprobar_identidad: true,
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const limit = 30;

  // Cierre definitivo (sin ROS) por lista de CVUs
  const [showCierreDialog, setShowCierreDialog] = useState(false);
  const [cierreCvus, setCierreCvus] = useState('');
  const [cierreBloquear, setCierreBloquear] = useState(false);
  const [cierreCerrarCasos, setCierreCerrarCasos] = useState(true);
  const [cerrando, setCerrando] = useState(false);
  const [cierreProgress, setCierreProgress] = useState(null);
  const [cierreResult, setCierreResult] = useState(null);
  const [pendientesManual, setPendientesManual] = useState([]);

  const loadPendientesManual = useCallback(async () => {
    try {
      const res = await api.get('/waya/cierre-manual-pendientes');
      setPendientesManual(res.data.items || []);
    } catch { /* ignore */ }
  }, []);

  const handleCierreSinRos = async () => {
    const cvus = cierreCvus.split(/[\n,;\s]+/).map((v) => v.trim()).filter(Boolean);
    if (cvus.length === 0) { toast.error('Pegá al menos un CVU'); return; }
    setCerrando(true);
    setCierreProgress({ done: 0, total: cvus.length });
    setCierreResult(null);
    try {
      const res = await api.post('/waya/cierre-definitivo-cvus', {
        cvus,
        bloquear_si_necesario: cierreBloquear,
        cerrar_casos: cierreCerrarCasos,
      });
      const jobId = res.data?.job_id;
      const total = res.data?.total || cvus.length;
      let job = null;
      for (let i = 0; i < 300; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const jr = await api.get(`/waya/cierre-definitivo-cvus-job/${jobId}`);
        job = jr.data;
        const done = Object.values(job?.progreso || {}).filter((p) => ['done', 'omitido', 'error', 'completado'].includes(p.estado) || p.paso_actual === 'CERRAR').length;
        setCierreProgress({ done, total });
        if (job?.estado === 'completado') break;
      }
      const r = job?.resultado || {};
      setCierreResult(r);
      if ((r.eliminadas || 0) > 0) {
        toast.success(`${r.eliminadas} cuenta(s) dada(s) de baja${r.no_ok?.length ? ` · ${r.no_ok.length} marcada(s) para revisión manual` : ''}`);
      } else {
        toast.warning(r.message || 'No se dio de baja ninguna cuenta');
      }
      loadCuentas();
      loadPendientesManual();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en el cierre definitivo');
    } finally {
      setCerrando(false);
      setCierreProgress(null);
    }
  };

  const handleResolverManual = async (item) => {
    try {
      await api.post(`/waya/cierre-manual-pendientes/${item.id}/resolver`);
      toast.success('Marcada como procesada manualmente');
      loadPendientesManual();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al resolver');
    }
  };

  const descargarPendientesCSV = () => {
    const rows = [['CVU', 'CVU Waya', 'Motivo', 'Fecha']];
    pendientesManual.forEach((p) => rows.push([p.cvu || '', p.cvu_waya || '', (p.motivo || '').replace(/[\n,]/g, ' '), p.created_at || '']));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cierre_manual_pendientes_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadCuentas = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEstado) params.append('estado', filterEstado);
      if (searchTerm) params.append('search', searchTerm);
      params.append('skip', page * limit);
      params.append('limit', limit);
      const res = await api.get(`/cuentas-juridicas/all?${params.toString()}`);
      setCuentas(res.data.cuentas || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error loading cuentas:', error);
    } finally {
      setLoading(false);
    }
  }, [filterEstado, searchTerm, page]);

  // Cargar stats globales (sin filtro)
  const loadStats = useCallback(async () => {
    try {
      const promises = ['PENDIENTE', 'CREADA', 'ERROR', 'CANCELADA'].map(estado =>
        api.get(`/cuentas-juridicas/all?estado=${estado}&limit=1`).then(r => ({ estado, total: r.data.total }))
      );
      const results = await Promise.all(promises);
      const totalAll = await api.get('/cuentas-juridicas/all?limit=1').then(r => r.data.total);
      const newStats = { total: totalAll, pendientes: 0, creadas: 0, error: 0, canceladas: 0 };
      for (const r of results) {
        if (r.estado === 'PENDIENTE') newStats.pendientes = r.total;
        else if (r.estado === 'CREADA') newStats.creadas = r.total;
        else if (r.estado === 'ERROR') newStats.error = r.total;
        else if (r.estado === 'CANCELADA') newStats.canceladas = r.total;
      }
      setStats(newStats);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadCuentas();
    loadStats();
  }, [loadCuentas, loadStats]);

  // Cargar configuración de límites
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/cuentas-juridicas/config/limites');
        const { limit_types, ...config } = res.data;
        setLimitsConfig(prev => ({ ...prev, ...config }));
      } catch { /* use defaults */ }
    })();
  }, []);

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put('/cuentas-juridicas/config/limites', limitsConfig);
      toast.success('Configuración guardada');
      setShowConfig(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingConfig(false);
    }
  };

  // Auto-refresh si hay pendientes
  useEffect(() => {
    const hasPending = cuentas.some(c => c.estado === 'PENDIENTE');
    if (!hasPending) return;
    const interval = setInterval(loadCuentas, 10000);
    return () => clearInterval(interval);
  }, [cuentas, loadCuentas]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setPage(0);
  };

  const handleCancelarIndividual = async (cuenta) => {
    if (!window.confirm(`¿Cancelar la cuenta "${cuenta.datos?.razon_social}"?`)) return;
    try {
      const payload = cuenta.id
        ? { cuenta_id: cuenta.id }
        : { empresa_id: cuenta.empresa_id, numero: cuenta.numero };
      await api.post('/cuentas-juridicas/cancelar-individual', payload);
      toast.success('Cuenta cancelada');
      loadCuentas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    }
  };

  const handleReprocesarIndividual = async (cuenta) => {
    if (!window.confirm(`¿Reprocesar la cuenta "${cuenta.datos?.razon_social}"?`)) return;
    try {
      const empresaId = cuenta.empresa_id;
      if (empresaId) {
        await api.post(`/cuentas-juridicas/empresa/${empresaId}/reprocesar`);
      } else {
        await api.post(`/cuentas-juridicas/${cuenta.onboarding_request_id}/reprocesar`);
      }
      toast.success('Reprocesamiento iniciado');
      loadCuentas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reprocesar');
    }
  };

  const handleActivarCuenta = async (cuenta) => {
    if (!cuenta.id) {
      toast.error('Esta cuenta no tiene ID');
      return;
    }
    const label = cuenta.resultado === 'OK_ACTIVADA' ? 'modificar los límites de' : 'activar';
    if (!window.confirm(`¿Desea ${label} la cuenta "${cuenta.datos?.razon_social}"?`)) return;
    try {
      const res = await api.post('/cuentas-juridicas/activar', {
        cuenta_id: cuenta.id,
        limites: {
          limite_diario: limitsConfig.limite_diario,
          limite_mensual: limitsConfig.limite_mensual,
          limite_alerta: limitsConfig.limite_alerta,
          limite_bloqueo_retiro: limitsConfig.limite_bloqueo_retiro,
          limite_bloqueo_cuenta: limitsConfig.limite_bloqueo_cuenta,
          limite_bloqueo_cvu: limitsConfig.limite_bloqueo_cvu,
        },
        aprobar_identidad: limitsConfig.aprobar_identidad,
      });
      toast.success(res.data.message || 'Activación iniciada');
      loadCuentas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al activar');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="cuentas-waya-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <PackageOpen className="text-emerald-600" size={28} />
            Cuentas Waya
          </h1>
          <p className="text-slate-500 text-sm mt-1">Cuentas jurídicas creadas en el portal Waya</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showConfig ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowConfig(!showConfig)}
            data-testid="toggle-config-btn"
          >
            <Settings size={14} className="mr-1" /> Configuración
          </Button>
          <Button variant="outline" size="sm" onClick={loadCuentas} data-testid="refresh-cuentas-btn">
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
          <Button
            size="sm"
            onClick={() => { setCierreCvus(''); setCierreBloquear(false); setCierreCerrarCasos(true); setCierreResult(null); setShowCierreDialog(true); }}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="cierre-sin-ros-btn"
          >
            <Trash2 size={14} className="mr-1" /> Cierre Definitivo (sin ROS)
          </Button>
        </div>
      </div>

      {/* Panel: pendientes de cierre manual */}
      {pendientesManual.length > 0 && (
        <Card className="p-4 border-2 border-amber-300 bg-amber-50/50" data-testid="pendientes-manual-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-amber-800 flex items-center gap-2">
              <AlertTriangle size={18} /> Pendientes de validación / cierre manual ({pendientesManual.length})
            </h3>
            <Button variant="outline" size="sm" onClick={descargarPendientesCSV} data-testid="descargar-pendientes-csv">
              <Download size={14} className="mr-1" /> Descargar CSV
            </Button>
          </div>
          <p className="text-xs text-amber-700 mb-3">
            No se pudo realizar el cierre definitivo de estas cuentas. Revisalas y procesalas manualmente en Waya; luego marcalas como resueltas.
          </p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {pendientesManual.map((p) => (
              <div key={p.id} className="flex items-center justify-between bg-white rounded-md border border-amber-200 px-3 py-2" data-testid={`pendiente-manual-${p.id}`}>
                <div className="text-sm">
                  <span className="font-mono text-slate-800">{p.cvu_waya || p.cvu}</span>
                  <span className="text-slate-500 ml-2">· {p.motivo || 'No se pudo'}</span>
                </div>
                <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300" onClick={() => handleResolverManual(p)} data-testid={`resolver-pendiente-${p.id}`}>
                  <CheckCircle size={14} className="mr-1" /> Marcar resuelta
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-5 gap-3">
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
          <p className="text-xs text-slate-500">Total</p>
        </Card>
        <Card className="p-4 text-center border-l-4 border-l-yellow-400">
          <p className="text-2xl font-bold text-yellow-700">{stats.pendientes}</p>
          <p className="text-xs text-slate-500">Pendientes</p>
        </Card>
        <Card className="p-4 text-center border-l-4 border-l-green-400">
          <p className="text-2xl font-bold text-green-700">{stats.creadas}</p>
          <p className="text-xs text-slate-500">Creadas</p>
        </Card>
        <Card className="p-4 text-center border-l-4 border-l-red-400">
          <p className="text-2xl font-bold text-red-700">{stats.error}</p>
          <p className="text-xs text-slate-500">Error</p>
        </Card>
        <Card className="p-4 text-center border-l-4 border-l-slate-400">
          <p className="text-2xl font-bold text-slate-600">{stats.canceladas}</p>
          <p className="text-xs text-slate-500">Canceladas</p>
        </Card>
      </div>

      {/* Panel de Configuración de Límites */}
      {showConfig && (
        <Card className="p-5 border-2 border-emerald-200 bg-emerald-50/30" data-testid="config-limites-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Settings size={18} className="text-emerald-600" />
              Valores por defecto para nuevas cuentas
            </h3>
            <Button size="sm" onClick={handleSaveConfig} disabled={savingConfig} data-testid="save-config-btn">
              <Save size={14} className="mr-1" /> {savingConfig ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Estos valores se muestran como predeterminados al crear nuevas cuentas. El usuario puede modificarlos antes de confirmar.
          </p>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(LIMIT_LABELS).map(([key, label]) => (
              <div key={key}>
                <label className="text-xs font-medium text-slate-600 block mb-1">{label}</label>
                <Input
                  type="text"
                  value={limitsConfig[key] || ''}
                  onChange={(e) => setLimitsConfig(prev => ({ ...prev, [key]: e.target.value }))}
                  placeholder="0.00"
                  data-testid={`config-${key}`}
                />
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              type="checkbox"
              id="aprobar_identidad"
              checked={limitsConfig.aprobar_identidad}
              onChange={(e) => setLimitsConfig(prev => ({ ...prev, aprobar_identidad: e.target.checked }))}
              className="rounded border-slate-300"
              data-testid="config-aprobar-identidad"
            />
            <label htmlFor="aprobar_identidad" className="text-sm text-slate-700">
              Aprobar validación de identidad automáticamente
            </label>
          </div>
        </Card>
      )}

      {/* Filtros */}
      <Card className="p-4">
        <div className="flex items-end gap-4 flex-wrap">
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
            <label className="text-xs text-slate-500 block mb-1">Buscar</label>
            <div className="flex gap-2">
              <Input
                placeholder="Razón social, CUIT, email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                data-testid="search-cuentas-input"
              />
              <Button type="submit" variant="outline" size="sm">
                <Search size={14} />
              </Button>
            </div>
          </form>
          <div className="w-40">
            <label className="text-xs text-slate-500 block mb-1">Estado</label>
            <Select value={filterEstado || 'all'} onValueChange={(v) => { setFilterEstado(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger data-testid="filter-estado-cuentas">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(ESTADOS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(filterEstado || searchTerm) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterEstado(''); setSearchTerm(''); setSearchInput(''); setPage(0); }}>
              Limpiar
            </Button>
          )}
        </div>
      </Card>

      {/* Tabla */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">#</TableHead>
              <TableHead>Razón Social</TableHead>
              <TableHead>CUIT</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Empresa</TableHead>
              <TableHead className="w-24">Estado</TableHead>
              <TableHead className="w-36">Creado</TableHead>
              <TableHead className="w-36">Procesado</TableHead>
              <TableHead className="w-24">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                  <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
                  Cargando...
                </TableCell>
              </TableRow>
            ) : cuentas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                  No se encontraron cuentas
                </TableCell>
              </TableRow>
            ) : (
              cuentas.map((cuenta, idx) => {
                const est = ESTADOS[cuenta.estado] || ESTADOS.PENDIENTE;
                const EstIcon = est.icon;
                return (
                  <TableRow key={cuenta.id || `${cuenta.empresa_id}-${cuenta.numero}-${idx}`} data-testid={`cuenta-row-${idx}`}>
                    <TableCell className="font-mono text-sm text-slate-500">{cuenta.numero}</TableCell>
                    <TableCell className="font-medium">{cuenta.datos?.razon_social || '-'}</TableCell>
                    <TableCell className="font-mono text-sm">{cuenta.datos?.cuit || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-600">{cuenta.datos?.email || '-'}</TableCell>
                    <TableCell>
                      {cuenta.empresa_id ? (
                        <button
                          className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                          onClick={() => navigate(`/m/empresas/empresa/${cuenta.empresa_id}`)}
                          data-testid={`empresa-link-${idx}`}
                        >
                          <Building2 size={12} />
                          {cuenta.empresa_nombre || cuenta.empresa_id.substring(0, 8)}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${est.color} text-xs flex items-center gap-1 w-fit`}>
                        <EstIcon size={10} />
                        {cuenta.resultado === 'OK_ACTIVADA' ? 'Activada' :
                         cuenta.resultado === 'OK_PARCIAL' ? 'Parcial' :
                         est.label}
                      </Badge>
                      {cuenta.error && (
                        <p className="text-xs text-red-500 mt-1 max-w-[180px] truncate" title={cuenta.error}>{cuenta.error}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {formatDate(cuenta.created_at)}
                      <br />
                      <span className="text-slate-400">{cuenta.creado_por_email}</span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {formatDate(cuenta.processed_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {cuenta.estado === 'PENDIENTE' && (
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 px-2 text-red-600 hover:text-red-700"
                            onClick={() => handleCancelarIndividual(cuenta)}
                            title="Cancelar"
                            data-testid={`cancelar-cuenta-${idx}`}
                          >
                            <Ban size={14} />
                          </Button>
                        )}
                        {cuenta.estado === 'ERROR' && (
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 px-2 text-orange-600 hover:text-orange-700"
                            onClick={() => handleReprocesarIndividual(cuenta)}
                            title="Reprocesar"
                            data-testid={`reprocesar-cuenta-${idx}`}
                          >
                            <RefreshCw size={14} />
                          </Button>
                        )}
                        {cuenta.estado === 'CREADA' && cuenta.resultado !== 'OK_ACTIVADA' && (
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 px-2 text-blue-600 hover:text-blue-700"
                            onClick={() => handleActivarCuenta(cuenta)}
                            title="Activar (identidad + límites)"
                            data-testid={`activar-cuenta-${idx}`}
                          >
                            <Zap size={14} />
                          </Button>
                        )}
                        {cuenta.estado === 'CREADA' && cuenta.resultado === 'OK_ACTIVADA' && (
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 px-2 text-slate-500 hover:text-slate-700"
                            onClick={() => handleActivarCuenta(cuenta)}
                            title="Modificar límites"
                            data-testid={`modificar-limites-${idx}`}
                          >
                            <Settings size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-slate-500">
              Mostrando {page * limit + 1}-{Math.min((page + 1) * limit, total)} de {total}
            </p>
            <div className="flex gap-1">
              <Button
                variant="outline" size="sm" disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                <ChevronLeft size={14} />
              </Button>
              <Button
                variant="outline" size="sm" disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Dialog: Cierre definitivo sin ROS por lista de CVUs */}
      <Dialog open={showCierreDialog} onOpenChange={(v) => { if (!cerrando) setShowCierreDialog(v); }}>
        <DialogContent className="max-w-lg" data-testid="cierre-sin-ros-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 size={20} /> Cierre definitivo (sin ROS)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>Pegá la lista de <strong>CVUs</strong> (uno por línea) para dar de baja la cuenta en Waya. Esta acción es <strong className="text-rose-700">irreversible</strong>.</p>
            <Textarea
              rows={6}
              placeholder={'0000000000000000000001\n0000000000000000000002'}
              value={cierreCvus}
              onChange={(e) => setCierreCvus(e.target.value)}
              disabled={cerrando}
              className="font-mono text-xs"
              data-testid="cierre-cvus-textarea"
            />
            <p className="text-xs text-slate-500">Por seguridad, solo se da de baja si la cuenta está <strong>bloqueada</strong>.</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={cierreBloquear} onCheckedChange={(v) => setCierreBloquear(!!v)} disabled={cerrando} data-testid="cierre-bloquear-check" />
              Bloquear automáticamente si no está bloqueada
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={cierreCerrarCasos} onCheckedChange={(v) => setCierreCerrarCasos(!!v)} disabled={cerrando} data-testid="cierre-cerrar-casos-check" />
              Cerrar los casos vinculados al finalizar
            </label>
            {cierreResult?.no_ok?.length > 0 && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-800" data-testid="cierre-result-no-ok">
                <AlertTriangle size={14} className="inline mr-1" />
                {cierreResult.no_ok.length} cuenta(s) no se pudieron cerrar y quedaron marcadas para revisión manual (ver panel superior).
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCierreDialog(false)} disabled={cerrando}>Cerrar</Button>
            <Button onClick={handleCierreSinRos} disabled={cerrando} className="bg-rose-600 hover:bg-rose-700" data-testid="cierre-sin-ros-confirm">
              {cerrando ? (cierreProgress ? `Procesando ${cierreProgress.done}/${cierreProgress.total}…` : 'Procesando…') : 'Dar de baja'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
